import CurrencyIcon from '@/components/common/CurrencyIcon';
import { convertedQtyFormatterConfig } from '@/config/config';
import { CollapsedBalances } from '@/state/reducers/balanceSlice';
import { isSameUTCDay } from '@/utils/format';
import { useCurrency } from '@/utils/hooks/useCurrency';
import { useCurrencySymbol } from '@/utils/hooks/useCurrencySymbol';
import { useDidUpdate } from '@/utils/hooks/useDidUpdate';
import { useDisclosure } from '@/utils/hooks/useDisclosure';
import useLanguage from '@/utils/hooks/useLanguage';
import usePreviousState from '@/utils/hooks/usePreviousState';
import { useSso } from '@/utils/providers/SSOProvider';
import cn from 'classnames';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNumberFormatter } from 'react-aria';
import { MdArrowCircleDown, MdArrowCircleUp, MdChevronRight, MdOutlineCalendarToday, MdSwapVert } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';

interface BalanceProps {
    balance: CollapsedBalances;
    nopBalance: { baseCurrency?: string };
    isNewlyAdded: boolean;
    isSameSettlementDate: boolean;
}

const Balance = ({ balance, nopBalance, isNewlyAdded, isSameSettlementDate }: BalanceProps) => {
    const disclosure = useDisclosure(false);
    const prevPosition = usePreviousState(balance.totalAmount);

    const timer = useRef<NodeJS.Timeout>();
    const [priceChange, setPriceChange] = useState({ increased: false, decreased: false });

    const ccyConfig = useCurrency(balance.currency);
    const baseCcyConfig = useCurrency(nopBalance?.baseCurrency || '');
    const baseCcyDecimals = Math.max(baseCcyConfig.order_decimals - 2, 0);

    const formatQty = useNumberFormatter(convertedQtyFormatterConfig);

    const formatBaseQty = useNumberFormatter({
        minimumFractionDigits: baseCcyDecimals,
        maximumFractionDigits: baseCcyDecimals
    });

    const handleEvents = useCallback(() => {
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(() => setPriceChange({ increased: false, decreased: false }), 3000);
    }, [timer.current]);

    const showSettlements = useMemo(() => {
        const hasSettlements = balance.rows.length > 0;
        const allSettlementsAreToday = balance.rows.every((row) => isSameUTCDay(row.valueDate));
        return hasSettlements && !allSettlementsAreToday;
    }, [balance.rows]);

    const shouldShowSettlements = useMemo(() => {
        return !isSameSettlementDate && showSettlements;
    }, [isSameSettlementDate, showSettlements]);

    useDidUpdate(() => {
        if (balance.totalAmount && prevPosition) {
            if (balance.totalAmount === prevPosition) {
                setPriceChange({ increased: false, decreased: false });
            } else if (balance.totalAmount > prevPosition) {
                setPriceChange({ increased: true, decreased: false });
                handleEvents();
            } else {
                setPriceChange({ increased: false, decreased: true });
                handleEvents();
            }
        }
    }, [balance.totalAmount]);

    useEffect(() => {
        if (isNewlyAdded) {
            if (balance.totalAmount > 0) {
                setPriceChange({ increased: true, decreased: false });
                handleEvents();
            } else if (balance.totalAmount < 0) {
                setPriceChange({ increased: false, decreased: true });
                handleEvents();
            }
        }
    }, [isNewlyAdded]);
    const symbol = useCurrencySymbol(nopBalance?.baseCurrency || '');

    return (
        <div className="flex flex-col transition-all duration-150 ease-in-out">
            <div
                className={cn('flex justify-between items-center p-2 hover:bg-brand-background-dark', {
                    ['cursor-pointer']: shouldShowSettlements
                })}
                onClick={() => {
                    if (shouldShowSettlements) disclosure[1].toggle();
                }}>
                <BalanceCurrency balance={balance} ccyConfig={ccyConfig} />
                <BalanceValue
                    shouldShowSettlements={shouldShowSettlements}
                    formatQty={formatQty}
                    formatBaseQty={formatBaseQty}
                    balance={balance}
                    ccyConfig={ccyConfig}
                    priceChange={priceChange}
                    symbol={symbol}
                    disclosure={disclosure}
                />
            </div>
            <SettlementDates
                shouldShowSettlements={shouldShowSettlements}
                disclosure={disclosure}
                balance={balance}
                symbol={symbol}
                formatQty={formatQty}
                formatBaseQty={formatBaseQty}
            />
        </div>
    );
};

export default Balance;

const BalanceCurrency = ({ balance, ccyConfig }: any) => {
    return (
        <div className="relative flex flex-col text-left flex-[1_1_0]">
            <div className={cn('flex gap-1.5 text-sm items-center', {})}>
                <CurrencyIcon ccy={balance.currency} className="w-5 h-5" />
                <div className="flex items-center gap-1 whitespace-nowrap">
                    <b>{balance.currency}</b>
                    <span className="text-2xs text-brand-neutral-400">{ccyConfig.longName}</span>
                </div>
            </div>
        </div>
    );
};

const BalanceValue = ({
    shouldShowSettlements,
    formatQty,
    formatBaseQty,
    balance,
    ccyConfig,
    priceChange,
    symbol,
    disclosure
}: any) => {
    const navigate = useNavigate();
    const { hasPermission } = useSso();

    const balanceColor = useMemo(() => {
        if (balance.totalAmount === 0) return 'text-brand-text';
        else if (balance.totalAmount > 0) return 'text-brand-green';
        else return 'text-brand-red';
    }, [balance.totalAmount]);

    const canDeposit = hasPermission('REQUEST_DEPOSIT');
    const canWithdraw = hasPermission('REQUEST_WITHDRAWAL');

    const isExpanded = disclosure[0];

    return (
        <div
            className={cn('flex flex-[2_1_0] relative justify-end group transition-all duration-150 ease-in-out', {
                [balanceColor]: true
            })}>
            <div
                className={cn('flex items-center gap-3', {
                    '-mr-[76px]': shouldShowSettlements,
                    '-mr-11': !shouldShowSettlements
                })}>
                <div
                    className={cn(
                        'flex flex-col justify-center items-end transition-all duration-150 ease-in-out flex-[2_1_0] group-hover:opacity-0'
                    )}>
                    <div className="relative w-fit text-sm font-bold flex items-center gap-1 transition-all duration-150 ease-in-out">
                        {formatQty.format(balance.totalAmount)}
                        <span className="absolute -left-6 top-0">
                            {priceChange.decreased && <MdSwapVert className={cn('w-4 h-4 text-brand-red')} />}
                            {priceChange.increased && <MdSwapVert className={cn('w-4 h-4 text-brand-green')} />}
                        </span>
                    </div>
                    {balance.totalAmountInBaseCurrency && (
                        <div className="flex items-center text-brand-neutral-400 text-xs transition-all duration-150 ease-in-out">
                            <div>
                                {balance.totalAmountInBaseCurrency > 0 ? '' : '-'}
                                {symbol}
                            </div>
                            {formatBaseQty.format(balance.totalAmountInBaseCurrency).replace('-', '')}
                        </div>
                    )}
                </div>
                {shouldShowSettlements && (
                    <div
                        className={`
                    w-5 h-5 rounded-full flex items-center text-brand-neutral-400 justify-center transition-all duration-200
                    ${
                        isExpanded
                            ? 'bg-brand-neutral-700 text-brand-neutral-200 -rotate-90'
                            : 'bg-brand-neutral-700 group-hover:text-brand-neutral-200 rotate-90'
                    }
                  `}>
                        <MdChevronRight size={16} />
                    </div>
                )}
                {/* {!shouldShowSettlements && <div className="w-6" />}{' '} */}
            </div>

            <div
                className={cn(
                    'flex items-center gap-1.5 opacity-0 group-hover:opacity-100 z-10 transition-all duration-150 ease-in-out',
                    { ['pr-8']: shouldShowSettlements }
                )}>
                <div
                    title="Deposit"
                    className={cn('flex justify-center items-center rounded-full', {
                        'text-brand-green hover:text-brand-green-light cursor-pointer': canDeposit,
                        'text-brand-neutral-400 cursor-not-allowed': !canDeposit
                    })}
                    onClick={() => {
                        if (!canDeposit) return;
                        navigate(`/portal/deposits?currency=${ccyConfig.show}`);
                    }}>
                    <MdArrowCircleUp className="w-5 h-5" />
                </div>
                <div
                    title="Withdraw"
                    className={cn('flex justify-center items-center rounded-full', {
                        'text-brand-red hover:text-brand-red-light cursor-pointer': canWithdraw,
                        'text-brand-neutral-400 cursor-not-allowed': !canWithdraw
                    })}
                    onClick={() => {
                        if (!canWithdraw) return;
                        navigate(`/portal/withdrawals?currency=${ccyConfig.show}`);
                    }}>
                    <MdArrowCircleDown className="w-5 h-5" />
                </div>
            </div>
        </div>
    );
};

const SettlementDates = ({ shouldShowSettlements, disclosure, balance, symbol, formatQty, formatBaseQty }: any) => {
    const lang = useLanguage();
    return (
        shouldShowSettlements &&
        disclosure[0] && (
            <div className="flex flex-col gap-1">
                <div className="flex justify-between text-2xs text-brand-neutral-400 px-2">
                    <div className="flex flex-[1_1_0]">
                        <div className="text-left flex gap-1 items-center flex-[1_1_0] pl-6 whitespace-nowrap uppercase">
                            <MdOutlineCalendarToday />
                            {lang.blotters.settlementDate}
                        </div>
                    </div>
                </div>
                <div className="flex flex-col mx-3 ml-8 pb-2">
                    {balance.rows.map((row, index) => (
                        <div
                            key={index}
                            className="flex justify-between text-xs px-2 bg-brand-neutral-700 rounded-sm p-1">
                            <div className="flex flex-[1_1_0]">
                                <div className="text-left whitespace-nowrap flex items-center gap-2">
                                    <div className="p-0.5 bg-brand-neutral-400 rounded-full" />
                                    {row.valueDate}
                                </div>
                            </div>
                            <div
                                className={cn(
                                    'flex flex-[1_1_0] relative items-center justify-center gap-2 text-right pr-[22px]',
                                    {
                                        ['text-brand-text']: row.amount === 0,
                                        ['text-brand-green']: row.amount > 0,
                                        ['text-brand-red']: row.amount < 0
                                    }
                                )}>
                                <div className="text-right flex-[1_1_0]">{formatQty.format(row.amount)}</div>
                                <div className="text-right flex-[1_1_0]">
                                    <div className="flex text-brand-neutral-400 w-full justify-end items-center">
                                        <div>
                                            {row.amountInBaseCurrency > 0 ? '' : '-'}
                                            {symbol}
                                        </div>
                                        {formatBaseQty.format(row.amountInBaseCurrency).replace('-', '')}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        )
    );
};
